import { useEffect, useState } from 'react';

type UseIsMobileOptions = {
  /** CSS breakpoint width in pixels. Defaults to 768 (typical tablet breakpoint). */
  breakpoint?: number;
};

/**
 * Hook: useIsMobile
 * - Returns `true` when the viewport width is less than the provided `breakpoint`.
 * - Uses `window.matchMedia` and cleans up listeners.
 * - Safe for SSR (returns `false` on server where `window` is undefined).
 */
type UseIsMobileReturn = {
  isMobile: boolean;
};

export default function useIsMobile({
  breakpoint = 768,
}: UseIsMobileOptions = {}): UseIsMobileReturn {
  const isClient =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function';

  const getMatches = () => {
    if (!isClient) {
      return false;
    }
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  };

  const [isMobile, setIsMobile] = useState<boolean>(getMatches);

  useEffect(() => {
    if (!isClient) {
      return undefined;
    }

    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (event: MediaQueryListEvent | MediaQueryList) => {
      // Some older browsers call listener with MediaQueryList, newer pass event
      const matches = 'matches' in event ? event.matches : mq.matches;
      setIsMobile(matches);
    };

    // Modern addEventListener
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler as EventListener);
    }

    return () => {
      if (typeof mq.removeEventListener === 'function') {
        mq.removeEventListener('change', handler as EventListener);
      }
    };
    // Re-run effect when breakpoint changes
  }, [breakpoint, isClient]);

  return { isMobile };
}
